import { ArrowRight } from "lucide-react";
import { Button, Input, Label } from "@bookdental/ui";
import { BRAZILIAN_STATES } from "@bookdental/schemas";
import { AvatarUpload } from "@/components/profile/AvatarUpload";
import { AddressFormFields, enderecoCompleto, type AddressFormValues } from "@/components/profile/AddressFormFields";

type Props = {
  avatarSrc?: string | null;
  onAvatarChange: () => void;
  userName: string;
  userPhone: string;
  cpf: string;
  croNumber: string;
  croState: string;
  /** Endereço da organização pessoal — "Endereço do consultório" ou "do laboratório". */
  address: AddressFormValues;
  rotuloDoEndereco: string;
  onAddressChange: (parcial: Partial<AddressFormValues>) => void;
  error: string | null;
  isPending: boolean;
  onUserNameChange: (v: string) => void;
  onUserPhoneChange: (v: string) => void;
  onCpfChange: (v: string) => void;
  onCroNumberChange: (v: string) => void;
  onCroStateChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack?: () => void;
};

export function ProfileStep({
  avatarSrc,
  onAvatarChange,
  userName,
  userPhone,
  cpf,
  croNumber,
  croState,
  address,
  rotuloDoEndereco,
  onAddressChange,
  error,
  isPending,
  onUserNameChange,
  onUserPhoneChange,
  onCpfChange,
  onCroNumberChange,
  onCroStateChange,
  onSubmit,
  onBack,
}: Props) {
  const canSubmit =
    !isPending &&
    userName.trim().length >= 2 &&
    cpf.replace(/\D/g, "").length === 11 &&
    croNumber.length >= 3 &&
    croState.length > 0 &&
    enderecoCompleto(address);

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="flex justify-center">
        <AvatarUpload src={avatarSrc} name={userName} onChange={onAvatarChange} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="onb-user-name">Seu nome</Label>
        <Input
          id="onb-user-name"
          placeholder="Ex: Dr. Joao Silva"
          value={userName}
          onChange={(e) => onUserNameChange(e.target.value)}
          required
          minLength={2}
          autoFocus
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="onb-cpf">CPF</Label>
        <Input
          id="onb-cpf"
          placeholder="000.000.000-00"
          inputMode="numeric"
          value={cpf}
          onChange={(e) => onCpfChange(e.target.value.replace(/\D/g, "").slice(0, 11))}
          required
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="onb-user-phone">Telefone (opcional)</Label>
        <Input
          id="onb-user-phone"
          placeholder="(11) 98765-4321"
          value={userPhone}
          onChange={(e) => onUserPhoneChange(e.target.value)}
        />
      </div>

      <div className="rounded-xl border border-zinc-100 bg-zinc-50/50 p-4">
        <p className="mb-3 text-sm font-medium text-zinc-700">Registro profissional (CRO)</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_100px]">
          <div className="space-y-1.5">
            <Label htmlFor="onb-cro-number">Numero CRO</Label>
            <Input
              id="onb-cro-number"
              placeholder="12345"
              inputMode="numeric"
              value={croNumber}
              onChange={(e) => onCroNumberChange(e.target.value.replace(/\D/g, ""))}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="onb-cro-state">UF</Label>
            <select
              id="onb-cro-state"
              value={croState}
              onChange={(e) => onCroStateChange(e.target.value)}
              required
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">—</option>
              {BRAZILIAN_STATES.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Sera marcado como <span className="font-medium">pendente</span> ate verificacao manual.
        </p>
      </div>

      <div className="space-y-2 border-t border-zinc-100 pt-4">
        <p className="text-sm font-semibold text-zinc-900">{rotuloDoEndereco}</p>
        <p className="text-xs text-zinc-500">
          Vai nas etiquetas e é o endereço de entrega dos pedidos de prótese. Só o complemento é opcional.
        </p>
        <AddressFormFields values={address} onChange={onAddressChange} />
      </div>

      {error && (
        <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="flex items-center justify-between pt-2">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-zinc-500 transition-colors hover:text-zinc-700"
          >
            Voltar
          </button>
        ) : (
          <span />
        )}
        <Button type="submit" disabled={!canSubmit}>
          {isPending ? "Salvando..." : "Continuar"}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </form>
  );
}
